import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {
	renderArrayField,
	renderAttachmentsPogis,
	renderDateString,
	renderLinkField,
	renderPogisDate,
} from 'components/EntityList/ColumnRenderers';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListColumnMetaData,
	InfinityListFilter,
} from 'components/InfinityList/InfinityList';
import {GetPogisDocumentsQueryQuery} from './hooks/PogisDocuments.generated';
import {decodeHTML} from 'entities';
import {PogisDocumentModified} from './PogisDocumentPage';
import {queryStringMongoArraySTD} from 'components/InfinityList/InfinitListStandardHooks';

export type IPogisPageFilterOptions = {
	(
		dataQuery: GetPogisDocumentsQueryQuery | undefined,
	): InfinityListColumnFilterOptions[];
};

export const usePogisPageColumn = () => {
	const {t} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'PogisDocumentsList',
	});

	const getValue = (item: string) => {
		return decodeHTML(item);
	};

	const widthSTD150 = {minWidth: 150, maxWidth: 200};
	const widthFixed150 = {minWidth: 150, maxWidth: 150};
	const filterArraySimple = {
		filterOnFilter: true,
		filterType: 'Array',
	};
	const filterArray = {
		...filterArraySimple,
		onRender: renderArrayField(),
	};
	const resizeMulti = {isResizable: true, isMultiline: true};

	const filterArray150Simple = {
		...widthSTD150,
		...resizeMulti,
		...filterArraySimple,
	};
	const filterArray150 = {...widthSTD150, ...resizeMulti, ...filterArray};

	const fixedSortableDateSimple = {
		...widthFixed150,
		sortable: true,
		filterType: 'Date',
	};
	const fixedSortableDate = {
		...fixedSortableDateSimple,
		onRender: renderDateString(),
	};
	const arrayNoFilter = {
		...widthFixed150,
		isMultiline: true,
		onRender: renderArrayField(),
	};

	const columns: InfinityListColumn[] = useMemo(
		() => [
			{
				key: 'pogisId',
				name: t('POGISID'),
				fieldName: 'pogisId',
				minWidth: 75,
				maxWidth: 75,
				sortable: true,
				...filterArraySimple,
			},
			{
				key: 'keywords',
				name: t('Keywords'),
				fieldName: 'keywords',
				...filterArray150,
			},
			{
				key: 'markets',
				name: t('Markets'),
				fieldName: 'markets',
				...filterArray150,
			},
			{
				key: 'standards',
				name: t('Standards'),
				fieldName: 'standards',
				...filterArray150Simple,
				onRender: renderArrayField(getValue),
			},
			{
				key: 'shortInfo',
				name: t('ShortInfo'),
				fieldName: 'shortInfo',
				minWidth: 400,
				maxWidth: 550,
				...resizeMulti,
				onRender(item: PogisDocumentModified) {
					return (
						<span
							dangerouslySetInnerHTML={{
								__html: item.shortInfo ?? '',
							}}
						></span>
					);
				},
			},
			{
				key: 'attachments',
				name: t('Attachments'),
				fieldName: 'pogisAttachments',
				...widthSTD150,
				...resizeMulti,
				onRender: renderAttachmentsPogis(),
			},
			{
				key: 'effectiveDate',
				name: t('Effective Date'),
				fieldName: 'effectiveDate',
				...fixedSortableDate,
			},
			{
				key: 'ntDate',
				name: t('NTDate'),
				fieldName: 'ntDate',
				...fixedSortableDate,
			},
			{
				key: 'nfDate',
				name: t('NFDate'),
				fieldName: 'nfDate',
				...fixedSortableDate,
			},
			{
				key: 'modelYear',
				name: t('ModelYear'),
				fieldName: 'modelYear',
				...widthFixed150,
				sortable: true,
				...filterArraySimple,
				onRender(item: PogisDocumentModified) {
					return (
						<div>
							{item.modelYear?.startsWith('1970')
								? ''
								: t('MY') + new Date(item?.modelYear).getFullYear()}
						</div>
					);
				},
			},
			{
				key: 'otherDates',
				name: t('Dates'),
				fieldName: 'otherDates',
				...widthFixed150,
				onRender: renderPogisDate(),
			},
			{
				key: 'regulationStatus',
				name: t('RegulationStatus'),
				fieldName: 'regulationStatus',
				sortable: true,
				...widthSTD150,
				isResizable: true,
				filterType: 'Array',
			},
			{
				key: 'editor',
				name: t('Editor'),
				fieldName: 'editor',
				sortable: true,
				...filterArray150Simple,
			},
			{
				key: 'associations',
				name: t('Associations'),
				fieldName: 'associations',
				...arrayNoFilter,
			},
			{
				key: 'relatedDocuments',
				name: t('RelatedDocuments'),
				fieldName: 'relatedDocuments',
				...arrayNoFilter,
			},
			{
				key: 'linkVersion',
				name: t('LinkVersion'),
				fieldName: 'linkVersion',
				...widthFixed150,
				onRender: renderLinkField(),
			},
			{
				key: 'docStatus',
				name: t('DocStatus'),
				fieldName: 'docStatus',
				...widthFixed150,
			},
		],
		[],
	);

	return columns;
};

const queryStringMongoDatePOGIS = (
	nameMongoDBField: string,
): ((filter: InfinityListFilter) => string) => {
	const handleConversion = (filter: InfinityListFilter) => {
		return `{dates:{$elemMatch:{dateType:'${nameMongoDBField}',date:{$${
			filter.operation
		}: ISODate('${filter.selection.slice(0, 10)}')  }}}}`;
	};

	return handleConversion;
};

const queryStringMongoMY = (): ((filter: InfinityListFilter) => string) => {
	const handleConversion = (filter: InfinityListFilter) => {
		const my = Number(filter.selection.slice(0, 4));
		return `{dates:{$elemMatch:{dateType:'Model Year',date:{$eq: ISODate('${my}-01-01T00:00:00.000Z')  }}}}`;
	};

	return handleConversion;
};

export const usePogisColumnMetaData = () => {
	const columnMetaData: InfinityListColumnMetaData[] = [
		{
			key: 'pogisId',
			mongoDBFn: queryStringMongoArraySTD(),
			linkName: 'id',
			sortInverted: true,
		},
		{
			key: 'keywords',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'markets',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'standards',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'regulationStatus',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'editor',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'effectiveDate',
			mongoDBFn: queryStringMongoDatePOGIS('Effective Date'),
			sortInverted: true,
		},
		{
			key: 'nfDate',
			mongoDBFn: queryStringMongoDatePOGIS('New Registration'),
			sortInverted: true,
		},
		{
			key: 'ntDate',
			mongoDBFn: queryStringMongoDatePOGIS('New Types'),
			sortInverted: true,
		},
		{
			key: 'modelYear',
			mongoDBFn: queryStringMongoMY(),
			sortInverted: true,
		},
	];

	return columnMetaData;
};

export const usePogisPageFilterOptions: IPogisPageFilterOptions = dataQuery => {
	const {t} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'PogisDocumentsList',
	});

	const queryData = dataQuery?.pogisDocumentsQuery.pogisFilterOptions;
	const columnFilterOptions: InfinityListColumnFilterOptions[] = useMemo(() => {
		const filterOptions = [
			{
				key: 'pogisId',
				fieldValuesUnique: (queryData?.pogisIdOptions ?? []).map(value => {
					return {optionKey: value, name: value};
				}),
			},
			{
				key: 'keywords',
				fieldValuesUnique: queryData?.keywordOptions,
			},
			{
				key: 'markets',
				fieldValuesUnique: queryData?.marketOptions,
			},
			{
				key: 'standards',
				fieldValuesUnique: (queryData?.standardOptions ?? []).map(x => {
					const standardName = x.name;
					return {optionKey: x.optionKey, name: decodeHTML(standardName)};
				}),
			},
			{
				key: 'regulationStatus',
				fieldValuesUnique: queryData?.regStatusOptions,
			},
			{
				key: 'editor',
				fieldValuesUnique: queryData?.editorOptions,
			},
			{
				key: 'modelYear',
				fieldValuesUnique: (queryData?.myOptions ?? [])
					.slice()
					.sort((a: string, b: string) => Number(b) - Number(a))
					.map(value => {
						return {optionKey: value, name: t('MY') + value};
					})
					.filter(x => x.optionKey !== ''),
			},
		];

		return filterOptions.map(x => {
			return {...x, fieldValuesUnique: x.fieldValuesUnique ?? []};
		});
	}, [dataQuery]);
	return columnFilterOptions;
};
